import type { LeagueHistory } from "@averagejoesff/shared";
import { fetchLeagueHistory } from "../api/awards";
import { useResource } from "./useResource";
import type { Resource } from "./useResource";

/**
 * How long a loaded history is reused as the visitor moves between pages.
 * Short next to the weekly refresh, long enough that Home -> Managers -> Home
 * doesn't download every game since 2016 three times.
 */
const REUSE_MS = 5 * 60 * 1000;

let cached: { loadedAt: number; promise: Promise<LeagueHistory | null> } | null = null;

function loadLeagueHistory(): Promise<LeagueHistory | null> {
  if (!cached || Date.now() - cached.loadedAt > REUSE_MS) {
    const entry = { loadedAt: Date.now(), promise: fetchLeagueHistory() };
    // Neither a failed load nor "nothing published yet" (null) is kept, so the next page asks again
    // instead of re-showing the error or the empty state for the rest of the reuse window.
    const forget = () => {
      if (cached === entry) cached = null;
    };
    entry.promise.then((history) => history === null && forget(), forget);
    cached = entry;
  }
  return cached.promise;
}

/**
 * The league's full history, fetched on first use (it's large, and the
 * Blowout/Dues/Payouts pages don't need it) and shared across pages.
 * `null` data means none has been published yet.
 */
export function useLeagueHistory(): Resource<LeagueHistory | null> {
  return useResource(loadLeagueHistory, [])[0];
}
